import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { RequestsService } from '../../services/requests/requests.abstract.service';
import { takeUntil, tap, switchMap, map, catchError } from 'rxjs/operators';
import { Subject, Observable, of, ReplaySubject, combineLatest } from 'rxjs';
import { Request, GetRequests } from '../../services/requests/requests.model';
import { MatDialog } from '@angular/material/dialog';
import { RequestDetailComponent } from '../../components/requests/detail/request-detail.component';
import { BcitiConfirmDialogComponent } from '../../components/dialogs/bciti-confirm-dialog/bciti-confirm-dialog.component';
import { RequestStatus } from '@bciti/types';
import { MessageService } from '../../services/message/message.abstract.service';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { languageSelector, organisationFeaturesConfigurationSelector } from '../../store/global.selectors';

@Component({
    selector: 'app-requests',
    templateUrl: './requests.component.html',
    styleUrls: ['./requests.component.scss'],
})
export class RequestComponent implements OnInit, OnDestroy {
    destroy$ = new Subject();
    pageUpdate$ = new ReplaySubject();

    loading = false;

    columnHeaders: any = {};
    actionMenu: any = {};
    requests: Request[] = [];
    requestList = [];

    pageOptions: any = {
        page: 0,
        pageSize: 10,
    };

    dateOptions = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' } as const;
    htmlProperties = ['isPublic'];

    requests$ = this.pageUpdate$.pipe(
        takeUntil(this.destroy$),
        tap(() => (this.loading = true)),
        switchMap(() =>
            this.service.getRequests(this.pageOptions.page, this.pageOptions.pageSize).pipe(
                takeUntil(this.destroy$),
                catchError((error) => {
                    this.messageService.error(this.translate.instant('messages.error'));
                    return of({ docs: [], page: 1, totalDocs: 0, limit: 0 });
                }),
                map((response) => response),
            ),
        ),
    );

    constructor(
        public translate: TranslateService,
        private service: RequestsService,
        private dialog: MatDialog,
        private messageService: MessageService,
        private store: Store<State>,
    ) {}

    public id: string;
    public btnText: string;
    public btnIcon: string;
    public btnCommand: string;

    ngOnInit() {
        combineLatest([this.store.select(languageSelector), this.store.select(organisationFeaturesConfigurationSelector)])
            .pipe(
                map(([lang, config]) => {
                    this.translate.use(lang);
                    this.columnHeaders = {
                        isPublic: this.translate.instant('pages.list.table.header.isPublic'),
                        requestNum: this.translate.instant('pages.list.table.header.requestNum'),
                        requestExternalId: this.translate.instant('pages.list.table.header.externalId'),
                        userName: this.translate.instant('pages.list.table.header.userName'),
                        category: this.translate.instant('pages.list.table.header.category'),
                        subject: this.translate.instant('pages.list.table.header.subject'),
                        updated_at: this.translate.instant('pages.list.table.header.updated_at'),
                        status: this.translate.instant('pages.list.table.header.status'),
                    };
                    this.actionMenu = {
                        heading: this.translate.instant('pages.list.table.action-menu.heading'),
                        options: [
                            {
                                label: this.translate.instant('pages.list.table.action-menu.options.detail'),
                                eventEmitterTag: 'details',
                            },
                            {
                                label: this.translate.instant('pages.list.table.action-menu.options.changeVisibility'),
                                eventEmitterTag: 'changeVisibility',
                            },
                        ],
                    };
                    if (config?.requests?.status?.enableAdminChanges) {
                        this.actionMenu.options.push({
                            label: this.translate.instant('pages.list.table.action-menu.options.changeStatus.toClosed'),
                            eventEmitterTag: 'changeStatusToClosed',
                        });
                    }
                }),
            )
            .subscribe();

        this.requests$.pipe(takeUntil(this.destroy$)).subscribe((response) => {
            this.pageOptions = {
                ...this.pageOptions,
                page: response.page - 1,
                length: response.totalDocs,
                pageSize: response.limit,
            };
            this.requestList = response.docs;
            this.requests = this.handleFormatRequests(response.docs);
            this.loading = false;
        });

        this.translate.onLangChange
            .pipe(
                takeUntil(this.destroy$),
                tap(() => this.pageUpdate$.next()),
            )
            .subscribe();

        this.pageUpdate$.next();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    handleActions(e) {
        switch (e.tag) {
            case 'details':
                this.viewDetails(e.element.id);
                break;
            case 'changeVisibility':
                this.changeVisibility(e.element.id);
                break;
            case 'changeStatusToClosed':
                const status = RequestStatus.CLOSED;
                this.changeStatus(e.element.id, status);
                break;
            default:
                console.warn('unknown action');
                break;
        }
    }

    handleFormatRequests(requests: Request[]) {
        const requestList = [];
        requests.forEach((req) => {
            const date = new Date(req.updated_at);
            const request = {
                id: req?._id,
                _id: req?._id,
                requestNum: req.requestNum,
                requestExternalId: req.external_id || '-',
                category: req?.category?.name,
                subject: req?.subject?.name,
                status: this.translate.instant('common.request.request.status.' + req.status),
                isPublic: this.getVisibilityIcon(req.isPublic),
                updated_at: new Intl.DateTimeFormat(`${this.translate.currentLang}-CA`, this.dateOptions).format(date),
                userName: `${req?.user?.firstName} ${req?.user?.lastName}`,
            };
            requestList.push(request);
        });
        return requestList;
    }

    pageChange($event) {
        this.pageOptions = {
            ...this.pageOptions,
            page: $event.pageIndex,
            pageSize: $event.pageSize,
            length: $event.length,
        };
        this.pageUpdate$.next();
    }

    viewDetails(id: string) {
        this.dialog.open(RequestDetailComponent, { data: { id } });
    }

    changeVisibility(id: string) {
        const request = this.requestList.find((request) => request._id === id);
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            maxWidth: '750px',
            data: {
                dialog: {
                    title: this.translate.instant('dialogs.request.changeVisibility.title'),
                    body: request.isPublic ? this.translate.instant('dialogs.request.changeVisibility.body.toPrivate') : this.translate.instant('dialogs.request.changeVisibility.body.toPublic'),
                    icon: '',
                    iconColor: '',
                    confirmLabel: this.translate.instant('dialogs.request.button.confirm'),
                    cancelLabel: this.translate.instant('dialogs.request.button.cancel'),
                    confirmButtonClass: 'bciti-button--blue',
                },
                component: {},
            },
        });

        dialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            dialogRef.componentInstance.loading = true;
            if (confirm) {
                this.modifyRequestVisibility(request._id, !request.isPublic);
                dialogRef.componentInstance.loading = false;
                dialogRef.close();
            } else {
                dialogRef.componentInstance.loading = false;
                dialogRef.close();
            }
        });
    }

    changeStatus(id: string, status: RequestStatus) {
        const request = this.requestList.find((request) => request._id === id);
        let body = '';
        if (status === RequestStatus.CLOSED) {
            body = this.translate.instant('dialogs.request.changeStatus.body.toClosed');
        }
        if (request.status === RequestStatus.CLOSED) {
            body = this.translate.instant('dialogs.request.changeStatus.body.alreadyClosed');
        }
        const dialogRef = this.dialog.open(BcitiConfirmDialogComponent, {
            maxWidth: '750px',
            data: {
                dialog: {
                    title: this.translate.instant('dialogs.request.changeStatus.title'),
                    body,
                    icon: '',
                    iconColor: '',
                    confirmLabel: this.translate.instant('dialogs.request.button.confirm'),
                    cancelLabel: this.translate.instant('dialogs.request.button.cancel'),
                    confirmButtonClass: 'bciti-button--blue',
                    hideActionButtons: request.status === RequestStatus.CLOSED,
                },
                component: {},
            },
        });
        dialogRef.componentInstance.onSubmit.subscribe((confirm) => {
            dialogRef.componentInstance.loading = true;
            if (confirm) {
                this.modifyRequestStatus(request._id, status);
                dialogRef.componentInstance.loading = false;
                dialogRef.close();
            } else {
                dialogRef.componentInstance.loading = false;
                dialogRef.close();
            }
        });
    }

    private modifyRequestVisibility(requestId: string, requestVisibility: boolean) {
        this.loading = true;
        const payload = {
            isPublic: requestVisibility,
        };
        this.service
            .modifyRequest(requestId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translate.instant('dialogs.request.changeVisibility.messages.success'));
                    this.requests.find((request) => request._id === response._id).isPublic = this.getVisibilityIcon(response.isPublic);
                    this.requestList.find((request) => request._id === response._id).isPublic = response.isPublic;
                    this.loading = false;
                },
                (error) => {
                    this.messageService.error(this.translate.instant('dialogs.request.changeVisibility.messages.error'));
                    this.loading = false;
                },
            );
    }

    private modifyRequestStatus(requestId: string, status: RequestStatus) {
        this.loading = true;
        const payload = {
            status,
        };
        this.service
            .modifyRequest(requestId, payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.messageService.success(this.translate.instant('dialogs.request.changeStatus.messages.success'));
                    this.requests.find((request) => request._id === response._id).status = this.translate.instant('common.request.request.status.' + response.status);
                    this.requestList.find((request) => request._id === response._id).status = response.status;
                    this.loading = false;
                },
                (error) => {
                    this.messageService.error(this.translate.instant('dialogs.request.changeStatus.messages.error'));
                    this.loading = false;
                },
            );
    }

    public getVisibilityIcon(isPublic) {
        return isPublic ? '<i class="icon-family"></i>' : '<i class="icon-view-off"></i>';
    }
}
